// Global packages and components
import { Link } from '@remix-run/react'
import { clsx } from 'clsx'
import {
  Blocks,
  BannerSidebar,
  Heading,
  InlineLink,
  BlockPostCarousel,
  BlockProductCarousel,
  Image,
  SchemaWrapper,
  SchemaMeta,
  SchemaElement,
} from '~/components'

// Types
import type {
  BannerSidebarProps,
  BlogListingItem,
  ProductSearchItem,
  SanityRefDeconstructed,
  SanityRefWithAlt,
} from '~/types'

interface Props {
  title: string
  author?: string
  createdAt?: string
  categories?: SanityRefDeconstructed[]
  fullWidthImage?: SanityRefWithAlt
  content?: any
  bannerSidebar?: BannerSidebarProps
  productCodes?: string[]
  /** The related products, resolved in the route loader */
  relatedProducts?: ProductSearchItem[] | null
  relatedPosts?: BlogListingItem[]
}

// Main export
const BlogSingle = ({
  title,
  author,
  createdAt,
  categories,
  fullWidthImage,
  content,
  bannerSidebar,
  productCodes,
  relatedProducts,
  relatedPosts,
}: Props) => (
  <SchemaWrapper
    as="article"
    type="BlogPosting"
  >
    {categories?.map(
      (c, i) =>
        c?.title && (
          <SchemaMeta
            key={i}
            prop="articleSection"
            content={c.title}
          />
        )
    )}
    <div className="container mb-8 pt-8 lg:mb-14 lg:pt-14">
      <div
        className={clsx({
          'mb-10': fullWidthImage,
          'mb-16': !fullWidthImage,
        })}
      >
        <Heading
          tag="h1"
          size="h2"
          className="text-center"
        >
          <SchemaElement prop="headline">{title}</SchemaElement>
        </Heading>

        <div className="mb-6 flex flex-col items-center justify-center gap-2 text-left md:flex-row md:gap-4">
          {author && (
            <p className="mb-0 text-sm">
              <span className="font-semibold">Written by:</span>&nbsp;
              <SchemaWrapper
                as="span"
                type="Person"
                prop="author"
              >
                <SchemaMeta
                  prop="name"
                  content={author}
                />
                {author}
              </SchemaWrapper>
            </p>
          )}
          {createdAt && (
            <p className="mb-0 text-sm">
              <span className="font-semibold">Date:</span>&nbsp;
              <SchemaElement
                as="time"
                prop="datePublished"
                dateTime={createdAt}
              >
                {createdAt}
              </SchemaElement>
            </p>
          )}
          {categories && (
            <p className="mb-0 text-sm">
              <span className="font-semibold">Categories:</span>&nbsp;
              {categories.map((e, i) => (
                <span key={i}>
                  {e?.slug?.current ? (
                    <Link
                      to={'/blog/' + e.slug.current}
                      className="text-sm text-teal-green underline"
                    >
                      {e?.title}
                    </Link>
                  ) : (
                    <span className="text-sm text-teal-green">{e?.title}</span>
                  )}
                  {i !== categories.length - 1 && <>,&nbsp;</>}
                </span>
              ))}
            </p>
          )}
        </div>
        <div className="flex justify-center">
          <InlineLink
            to="/blog"
            label="Back to blog"
            icon="arrowLeftIcon"
          />
        </div>
      </div>
      {fullWidthImage?.asset && (
        <div className="mb-8 aspect-[22/9] overflow-hidden rounded-2xl lg:mb-12">
          <Image
            image={fullWidthImage}
            className="h-full w-full object-cover mix-blend-multiply"
          />
        </div>
      )}
      <div className="mb-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <SchemaWrapper
          prop="articleBody"
          className="col-span-9 -mx-6"
        >
          {content && <Blocks blocks={content} />}
        </SchemaWrapper>

        {bannerSidebar && (
          <div className="col-span-3">
            <div className="mb-10 hidden lg:block">
              <BannerSidebar {...bannerSidebar} />
            </div>
          </div>
        )}
      </div>
      {(productCodes || relatedPosts) && (
        <div className="mb-16">
          <hr />
        </div>
      )}
      {productCodes && (
        <div className="mb-16">
          <BlockProductCarousel
            title="Related Products"
            productCodes={productCodes}
            serverProducts={relatedProducts}
          />
        </div>
      )}
      {relatedPosts && (
        <BlockPostCarousel
          title="Related Articles"
          items={relatedPosts}
        />
      )}
    </div>
  </SchemaWrapper>
)

export default BlogSingle
